import { useState, useCallback, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { canCreateBreakoutRooms } from "./videoCallUtils";

/**
 * Custom hook for managing breakout rooms
 * Handles fetching, creating, and managing breakout rooms
 */
export const useBreakoutRooms = (classId) => {
  const [breakoutRooms, setBreakoutRooms] = useState([]);
  const [hasBreakoutPermission, setHasBreakoutPermission] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  /**
   * Fetches all breakout rooms for a class
   */
  const fetchBreakoutRooms = useCallback(async () => {
    if (!classId) return;

    try {
      const breakoutRef = collection(
        db,
        "conference_calls",
        classId,
        "breakout_rooms",
      );
      const querySnapshot = await getDocs(breakoutRef);

      setBreakoutRooms(
        querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    } catch (error) {
      console.error("[useBreakoutRooms] Error fetching breakout rooms:", error);
    }
  }, [classId]);

  /**
   * Creates breakout rooms for a class
   */
  const createBreakoutRooms = useCallback(
    async (numRooms, roomDuration, availableSlots) => {
      if (!classId) return;

      setIsCreating(true);

      try {
        const conferenceDocRef = doc(db, "conference_calls", classId);
        const breakoutRoomsRef = collection(
          conferenceDocRef,
          "breakout_rooms",
        );

        for (let i = 0; i < numRooms; i++) {
          const newRoomRef = await addDoc(breakoutRoomsRef, {
            availableSlots,
            classEndTime: null,
            startedAt: null,
            roomDuration,
            roomMembers: [],
            createdAt: Timestamp.now(),
            roomName: `Breakout Room ${i + 1}`,
          });

          await updateDoc(newRoomRef, { roomId: newRoomRef.id });
        }

        // Refresh the list
        await fetchBreakoutRooms();
      } catch (error) {
        console.error(
          "[useBreakoutRooms] Error creating breakout rooms:",
          error,
        );
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [classId, fetchBreakoutRooms],
  );

  /**
   * Deletes a breakout room
   */
  const deleteBreakoutRoom = useCallback(
    async (roomId) => {
      if (!classId || !roomId) return;

      try {
        const roomRef = doc(
          db,
          "conference_calls",
          classId,
          "breakout_rooms",
          roomId,
        );
        await updateDoc(roomRef, {
          roomMembers: [],
          classEndTime: Timestamp.now(),
        });

        // Refresh the list
        await fetchBreakoutRooms();
      } catch (error) {
        console.error(
          "[useBreakoutRooms] Error deleting breakout room:",
          error,
        );
        throw error;
      }
    },
    [classId, fetchBreakoutRooms],
  );

  // Check permissions and fetch rooms on mount
  useEffect(() => {
    if (classId) {
      setHasBreakoutPermission(canCreateBreakoutRooms(classId));
      fetchBreakoutRooms();
    }
  }, [classId, fetchBreakoutRooms]);

  return {
    breakoutRooms,
    hasBreakoutPermission,
    isCreating,
    fetchBreakoutRooms,
    createBreakoutRooms,
    deleteBreakoutRoom,
  };
};

